import React, { useState, useEffect } from 'react';
import { listUsers, banUser, unbanUser, getUserWallet } from '../../services/adminMarketplaceService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../../context/ToastContext';

const WalletModal = ({ userId, onClose }) => {
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    getUserWallet(userId).then(setWallet).catch(() => setWallet({ error: true }));
  }, [userId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold">Wallet</h3>
          <button onClick={onClose} className="text-gray-500">Close</button>
        </div>
        {!wallet ? <p>Loading...</p> : wallet.error ? <p className="text-gray-500">This user has no wallet.</p> : (
          <>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Earnings (withdrawable)</p>
                <p className="text-xl font-bold">₦{wallet.earningsBalance.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Top-up credit</p>
                <p className="text-xl font-bold">₦{wallet.topUpBalance.toLocaleString()}</p>
              </div>
            </div>
            {wallet.bankAccount && (
              <p className="text-sm">Payout account: {wallet.bankAccount.accountName} — {wallet.bankAccount.bankName}</p>
            )}
            <div>
              <p className="font-semibold text-sm mb-1">Recent activity</p>
              <ul className="text-sm divide-y">
                {wallet.history.map(h => (
                  <li key={h._id} className="py-1 flex justify-between">
                    <span>{h.notes || h.type}</span>
                    <span className={h.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {h.amount >= 0 ? '+' : ''}₦{h.amount.toLocaleString()}
                    </span>
                  </li>
                ))}
                {wallet.history.length === 0 && <li className="py-1 text-gray-500">No activity yet.</li>}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const UserManagement = () => {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [walletUserId, setWalletUserId] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [banReason, setBanReason] = useState('');

  const refresh = () => listUsers({ search }).then(setUsers);
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmBan = async () => {
    await banUser(banTarget._id, banReason);
    setBanTarget(null);
    setBanReason('');
    toast.success(`${banTarget.email} has been banned`);
    refresh();
  };

  const handleUnban = async (id, email) => {
    await unbanUser(id);
    toast.success(`${email} has been unbanned`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Users</h2>
      <div className="flex gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or phone"
          className="p-2 border rounded-lg flex-1" />
        <button onClick={refresh} className="bg-black text-white px-4 py-2 rounded-lg">Search</button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="p-3">Email</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u._id} className="border-b">
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.role}</td>
                <td className="p-3">{u.status}</td>
                <td className="p-3 space-x-3">
                  {['therapist', 'venue'].includes(u.role) && (
                    <button onClick={() => setWalletUserId(u._id)} className="text-blue-600">Wallet</button>
                  )}
                  {u.status !== 'banned' ? (
                    <button onClick={() => setBanTarget(u)} className="text-red-600">Ban</button>
                  ) : (
                    <button onClick={() => handleUnban(u._id, u.email)} className="text-green-600">Unban</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {walletUserId && <WalletModal userId={walletUserId} onClose={() => setWalletUserId(null)} />}

      <ConfirmDialog
        open={!!banTarget}
        title={`Ban ${banTarget?.email || 'this user'}?`}
        message="They will lose access to the platform immediately. This can be reversed later."
        confirmLabel="Ban user"
        danger
        reason={banReason}
        onReasonChange={setBanReason}
        reasonLabel="Ban reason"
        reasonPlaceholder="Why is this account being banned?"
        reasonRequired
        onConfirm={confirmBan}
        onCancel={() => { setBanTarget(null); setBanReason(''); }}
      />
    </div>
  );
};

export default UserManagement;
